import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './NotificationsModal.css';
import { useAuth } from '../../contexts/AuthContext';
import { getUserNotifications, markNotificationRead, ORDER_STATUS } from '../../firebase/orders';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';

// ─── Statuses that mean the order has already moved past checkout ─────────────
const PAST_CHECKOUT_STATUSES = new Set([
    ORDER_STATUS.AWAITING_PAYMENT,
    ORDER_STATUS.IN_PRODUCTION,
    ORDER_STATUS.TO_BE_DELIVERED,
    ORDER_STATUS.DELIVERED,
    ORDER_STATUS.CANCELLED,
    'awaiting_payment',
    'in_production',
    'to_be_delivered',
    'delivered',
    'cancelled',
    'processing',
    'completed',
].map(status => String(status || '').trim().toLowerCase()));

// ─── Status icon + color config ───────────────────────────────────────────────
const STATUS_NOTIF_CONFIG = {
    [ORDER_STATUS.APPROVED]: {
        icon: '✓',
        bg:   '#e3f0ff',
        color:'#1a5faa',
        label:'Order Approved',
        actionLabel: 'Proceed to Checkout →',
        isActionable: true,
    },
    [ORDER_STATUS.AWAITING_PAYMENT]: {
        icon: '⏳',
        bg:   '#f3e5f5',
        color:'#7b1fa2',
        label:'Awaiting Payment Confirmation',
        actionLabel: null,
        isActionable: false,
    },
    [ORDER_STATUS.IN_PRODUCTION]: {
        icon: '⚙',
        bg:   '#e6f4ea',
        color:'#2d7a3a',
        label:'Order In Production',
        actionLabel: null,
        isActionable: false,
    },
    [ORDER_STATUS.TO_BE_DELIVERED]: {
        icon: '📦',
        bg:   '#fff3e0',
        color:'#e65100',
        label:'Ready for Pickup / Delivery',
        actionLabel: 'View My Orders',
        isActionable: true,
    },
    [ORDER_STATUS.DELIVERED]: {
        icon: '✅',
        bg:   '#e8f5e9',
        color:'#1b5e20',
        label:'Order Delivered',
        actionLabel: 'View My Orders',
        isActionable: true,
    },
    [ORDER_STATUS.CANCELLED]: {
        icon: '✕',
        bg:   '#fdecea',
        color:'#b71c1c',
        label:'Order Cancelled',
        actionLabel: 'View My Orders',
        isActionable: true,
    },
    'date_updated': {
        icon: '📅',
        bg:   '#e3f0ff',
        color:'#1a5faa',
        label:'Estimated To Be Delivered Date Updated',
        actionLabel: 'View My Orders',
        isActionable: true,
    },
};

function getConfig(status) {
    return STATUS_NOTIF_CONFIG[status] || {
        icon:  '🔔',
        bg:    '#f5f5f5',
        color: '#555',
        label: status || 'Notification',
        actionLabel: null,
        isActionable: false,
    };
}

function isPastCheckoutStatus(status) {
    return PAST_CHECKOUT_STATUSES.has(String(status || '').trim().toLowerCase());
}

function getNotificationConfig(notif) {
    const baseConfig = getConfig(notif.status);

    if (notif.status === ORDER_STATUS.APPROVED && notif.checkoutAvailable === false) {
        return {
            ...baseConfig,
            actionLabel: 'View My Orders',
            isActionable: true,
        };
    }

    return baseConfig;
}

function formatTime(value) {
    if (!value) return '';
    const date = value?.toDate ? value.toDate() : new Date(value);
    if (isNaN(date.getTime())) return '';
    const now    = Date.now();
    const diff   = now - date.getTime();
    const mins   = Math.floor(diff / 60000);
    const hours  = Math.floor(diff / 3600000);
    const days   = Math.floor(diff / 86400000);
    if (mins < 1)   return 'Just now';
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7)   return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month:'short', day:'numeric' });
}

// ─── Fetch the current live status of an order from Firestore ─────────────────
async function fetchCurrentOrderStatus(orderId) {
    try {
        const q    = query(collection(db, 'orders'), where('orderId', '==', orderId));
        const snap = await getDocs(q);
        if (!snap.empty) return snap.docs[0].data().status || null;
    } catch (err) {
        console.error('Error fetching order status:', err);
    }
    return null;
}

// ─── Mark a notification as cleared in Firestore ─────────────────────────────
// Path: notifications/{userId}/items/{notifId}
async function clearNotificationDoc(userId, notifId) {
    try {
        const ref = doc(db, 'notifications', userId, 'items', notifId);
        await updateDoc(ref, { cleared: true });
    } catch (err) {
        console.warn('Could not clear notification:', notifId, err);
    }
}

function NotificationsModal({ isOpen, onClose }) {
    const navigate        = useNavigate();
    const { currentUser } = useAuth();

    const [notifications, setNotifications] = useState([]);
    const [loading,       setLoading]       = useState(false);

    // ── Fetch from Firestore ──────────────────────────────────────────────────
    const loadNotifications = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        const { notifications: fetched } = await getUserNotifications(currentUser.uid);
        // Filter out any notifications previously cleared by the user
        const visible = (fetched || []).filter(n => !n.cleared);
        const withLiveStatus = await Promise.all(visible.map(async (notif) => {
            if (notif.status !== ORDER_STATUS.APPROVED || !notif.orderId) {
                return notif;
            }

            const liveOrderStatus = await fetchCurrentOrderStatus(notif.orderId);
            return {
                ...notif,
                liveOrderStatus,
                checkoutAvailable: liveOrderStatus
                    ? !isPastCheckoutStatus(liveOrderStatus)
                    : false,
            };
        }));

        setNotifications(withLiveStatus);
        setLoading(false);
    }, [currentUser]);

    useEffect(() => {
        if (isOpen) loadNotifications();
    }, [isOpen, loadNotifications]);

    const unreadCount = notifications.filter(n => !n.read).length;

    // ── Handle notification click ─────────────────────────────────────────────
    const handleClick = async (notif) => {
        if (!notif.read && currentUser) {
            await markNotificationRead(currentUser.uid, notif.id);
            setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
        }

        const cfg = getNotificationConfig(notif);
        if (!cfg.isActionable) return;

        if (notif.status === ORDER_STATUS.APPROVED && notif.orderId) {
            const currentStatus = notif.liveOrderStatus || await fetchCurrentOrderStatus(notif.orderId);
            if (!currentStatus || isPastCheckoutStatus(currentStatus)) {
                navigate('/account/purchase-history');
                onClose();
                return;
            }
            navigate('/checkout', { state: { orderId: notif.orderId } });
            onClose();
            return;
        }

        navigate('/account/purchase-history', {
            state: { tab: notif.status === ORDER_STATUS.CANCELLED ? 'cancelled' : null }
        });
        onClose();
    };

    // ── Mark all as read ──────────────────────────────────────────────────────
    const handleMarkAllRead = async () => {
        if (!currentUser) return;
        const unread = notifications.filter(n => !n.read);
        await Promise.all(unread.map(n => markNotificationRead(currentUser.uid, n.id)));
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    };

    // ── Clear read notifications — keeps unread and approved notifications ────
    const handleClearRead = async () => {
        if (!currentUser) return;

        // Keep only unread notifications and approved notifications that can still proceed to checkout.
        const toDelete = notifications.filter(n => {
            const isRead = n.read === true;
            const shouldKeepApproved = (
                n.status === ORDER_STATUS.APPROVED
                && n.checkoutAvailable === true
            );
            return isRead && !shouldKeepApproved;
        });

        // Mark each as cleared in Firestore so they don't come back on reload
        await Promise.all(toDelete.map(n => clearNotificationDoc(currentUser.uid, n.id)));

        // Update local state immediately using the same actionable rule.
        setNotifications(prev => prev.filter(n => {
            const isRead = n.read === true;
            const shouldKeepApproved = (
                n.status === ORDER_STATUS.APPROVED
                && n.checkoutAvailable === true
            );
            return !isRead || shouldKeepApproved;
        }));
    };

    if (!isOpen) return null;

    return (
        <div className="notifications-overlay" onClick={onClose}>
            <div className="notifications-modal-wrapper" onClick={e => e.stopPropagation()}>
                <button className="notifications-close-btn" onClick={onClose} aria-label="Close">✕</button>

                <div className="notifications-modal">
                    {/* Header */}
                    <div className="notifications-header">
                        <div className="notifications-header-left">
                            <h2 className="notifications-title">Notifications</h2>
                            {unreadCount > 0 && (
                                <span className="notifications-unread-badge">{unreadCount} new</span>
                            )}
                        </div>
                        {unreadCount > 0 && (
                            <button className="notif-mark-all-btn" onClick={handleMarkAllRead}>
                                Mark all as read
                            </button>
                        )}
                        {unreadCount === 0 && notifications.length > 0 && (
                            <button className="notif-clear-read-btn" onClick={handleClearRead}>
                                Clear all read
                            </button>
                        )}
                    </div>

                    {/* Body */}
                    <div className="notifications-body">
                        {loading ? (
                            <div className="notifications-loading">
                                <div className="notif-spinner" />
                                <span>Loading…</span>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="empty-notifications">
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ddd" strokeWidth="1.5">
                                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                                </svg>
                                <p>No notifications yet</p>
                                <span>You'll be notified when your order status changes.</span>
                            </div>
                        ) : (
                            <div className="notifications-list">
                                {notifications.map((notif) => {
                                    const cfg = getNotificationConfig(notif);
                                    return (
                                        <div
                                            key={notif.id}
                                            className={`notification-item ${!notif.read ? 'unread' : ''} ${cfg.isActionable ? 'actionable' : ''}`}
                                            onClick={() => handleClick(notif)}
                                        >
                                            {!notif.read && <div className="notif-unread-dot" />}

                                            <div
                                                className="notification-icon"
                                                style={{ background: cfg.bg, color: cfg.color }}
                                            >
                                                {cfg.icon}
                                            </div>

                                            <div className="notification-body-content">
                                                <div className="notification-top-row">
                                                    <h3 className="notification-title-text">{notif.title || cfg.label}</h3>
                                                    <span className="notification-time">{formatTime(notif.createdAt)}</span>
                                                </div>
                                                <p className="notification-message">{notif.message}</p>
                                                {notif.orderId && (
                                                    <span className="notification-order-id">#{notif.orderId}</span>
                                                )}
                                                {cfg.isActionable && cfg.actionLabel && (
                                                    <span className="notification-action-label" style={{ color: cfg.color }}>
                                                        {cfg.actionLabel}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer removed — actions moved to header */}
                </div>
            </div>
        </div>
    );
}

export default NotificationsModal;
